import { useState } from "react";
import {
  Download,
  FileText,
  Globe,
  Code,
  Table,
  ChevronDown,
  Check,
  Loader2,
} from "lucide-react";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@asm/ui";
import { exportReport, type ExportFormat, type ExportMetadata } from "@/lib/export-report";
import type { SimplifiedResults } from "@/lib/simplify-results";

interface ExportMenuProps {
  results: SimplifiedResults;
  metadata: ExportMetadata;
  className?: string;
}

const EXPORT_OPTIONS: Array<{
  format: ExportFormat;
  label: string;
  description: string;
  icon: typeof FileText;
}> = [
  {
    format: "pdf",
    label: "PDF Report",
    description: "Print-ready document",
    icon: FileText,
  },
  {
    format: "html",
    label: "HTML Report",
    description: "Viewable in browser",
    icon: Globe,
  },
  {
    format: "json",
    label: "JSON Data",
    description: "Structured data export",
    icon: Code,
  },
  {
    format: "csv",
    label: "CSV Spreadsheet",
    description: "Findings table for Excel",
    icon: Table,
  },
];

export function ExportMenu({ results, metadata, className = "" }: ExportMenuProps) {
  const [exporting, setExporting] = useState<ExportFormat | null>(null);
  const [lastExported, setLastExported] = useState<ExportFormat | null>(null);

  const handleExport = async (format: ExportFormat) => {
    setExporting(format);

    // Small delay to show loading state
    await new Promise((resolve) => setTimeout(resolve, 100));

    try {
      exportReport(results, metadata, format);
      setLastExported(format);

      // Clear the "last exported" indicator after a few seconds
      setTimeout(() => setLastExported(null), 3000);
    } catch (error) {
      console.error("Export failed:", error);
    } finally {
      setExporting(null);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={`gap-2 ${className}`}
          disabled={exporting !== null}
        >
          {exporting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
          Export Report
          <ChevronDown className="h-3 w-3 opacity-50" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
          Choose export format
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {EXPORT_OPTIONS.map((option) => {
          const Icon = option.icon;
          const isExporting = exporting === option.format;
          const wasExported = lastExported === option.format;

          return (
            <DropdownMenuItem
              key={option.format}
              onClick={() => handleExport(option.format)}
              disabled={isExporting}
              className="cursor-pointer"
            >
              <div className="flex items-center gap-3 w-full">
                <div className="p-1.5 rounded bg-muted">
                  {isExporting ? (
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  ) : wasExported ? (
                    <Check className="h-4 w-4 text-emerald-500" />
                  ) : (
                    <Icon className="h-4 w-4 text-muted-foreground" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">{option.label}</div>
                  <div className="text-xs text-muted-foreground">
                    {option.description}
                  </div>
                </div>
              </div>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default ExportMenu;
